// client/src/components/OnboardingDialog.js
import {
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, Button, FormControl,
  FormLabel, Input, VStack, Box
} from '@chakra-ui/react';
import { fetchWithAuth } from '../context/AuthContext';
import { useState } from 'react';
import Select from 'react-select';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Country, City } from 'country-state-city';

const API_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

const FIELD_OPTIONS = [
  { value: 'mechanical_engineering', label: 'Mechanical Engineering' },
  { value: 'civil_engineering', label: 'Civil Engineering' },
  { value: 'production_engineering', label: 'Production Engineering' },
  { value: 'electrical_engineering', label: 'Electrical Engineering' },
  { value: 'robotics_engineering', label: 'Robotics Engineering' },
  { value: 'designer', label: 'Designer' },
  { value: 'art_consultant', label: 'Art Consultant' },
  { value: 'fabricator', label: 'Fabricator' },
  { value: 'other', label: 'Other' },
];

const DISCOVERY_OPTIONS = [
  { value: 'Google Search', label: 'Google Search' },
  { value: 'Social Media (Instagram/LinkedIn/Twitter)', label: 'Social Media (Instagram/LinkedIn/Twitter)' },
  { value: 'Friend / Word of mouth', label: 'Friend / Word of mouth' },
  { value: 'YouTube', label: 'YouTube' },
  { value: 'Advertisement', label: 'Advertisement' },
  { value: 'other', label: 'Other' },
];

// Build country options once
const COUNTRY_OPTIONS = Country.getAllCountries().map(c => ({
  value: c.isoCode,
  label: c.name,
}));

export default function OnboardingDialog({ isOpen, user, onComplete }) {
  const [form, setForm] = useState({
    fullName: user?.fullName || '',
    phone: '',
    field: null,
    fieldOther: '',
    discoverySource: null,
    discoveryOther: '',
    country: null,
    city: null,
  });
  const [cityOptions, setCityOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleCountryChange = (selected) => {
    setForm(prev => ({ ...prev, country: selected, city: null }));
    if (selected) {
      const cities = City.getCitiesOfCountry(selected.value).map(c => ({
        value: c.name,
        label: c.name,
      }));
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const payload = {
        fullName: form.fullName,
        phone: form.phone || null,
        field: form.field?.value === 'other' ? null : form.field?.value || null,
        fieldOther: form.field?.value === 'other' ? form.fieldOther : null,
        discoverySource: form.discoverySource?.value === 'other'
          ? form.discoveryOther
          : form.discoverySource?.label || null,
        country: form.country?.label || null,
        region: form.city?.value || null, // maps to region field in schema
        onboardingCompleted: true,
      };
      const res = await fetchWithAuth(`${API_URL}/api/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save');
      onComplete(); // close dialog + proceed to Home
    } catch (err) {
      console.error('Onboarding save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // react-select custom styles to match Chakra UI
  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '12px',
      borderColor: '#E0E0E0',
      minHeight: '44px',
      fontSize: '14px',
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { }} // prevent closing without completing
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="lg"
      isCentered
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="16px" p={2}>
        <ModalHeader fontSize="20px" fontWeight="bold">
          Complete your profile
        </ModalHeader>

        <ModalBody>
          <VStack spacing={5} align="stretch">

            {/* Full Name */}
            <FormControl>
              <FormLabel fontSize="14px">Display name</FormLabel>
              <Input
                value={form.fullName}
                onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                h="44px"
                borderRadius="12px"
                borderColor="#E0E0E0"
                _focus={{ borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }}
              />
            </FormControl>

            {/* Phone */}
            <FormControl>
              <FormLabel fontSize="14px">Phone number</FormLabel>
              <Box
                border="1px solid #E0E0E0"
                borderRadius="12px"
                px={3}
                h="44px"
                display="flex"
                alignItems="center"
                _focusWithin={{ borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }}
              >
                <PhoneInput
                  international
                  defaultCountry={form.country?.value || 'IN'}
                  value={form.phone}
                  onChange={val => setForm(p => ({ ...p, phone: val }))}
                  style={{ width: '100%', fontSize: '14px', border: 'none', outline: 'none' }}
                />
              </Box>
            </FormControl>

            {/* Field / Role */}
            <FormControl>
              <FormLabel fontSize="14px">Your field</FormLabel>
              <Select
                options={FIELD_OPTIONS}
                value={form.field}
                onChange={val => setForm(p => ({ ...p, field: val, fieldOther: '' }))}
                placeholder="Select your field..."
                styles={selectStyles}
                isSearchable
              />
              {form.field?.value === 'other' && (
                <Input
                  mt={2}
                  placeholder="Please specify your field"
                  value={form.fieldOther}
                  onChange={e => setForm(p => ({ ...p, fieldOther: e.target.value }))}
                  h="44px"
                  borderRadius="12px"
                  borderColor="#E0E0E0"
                  _focus={{ borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }}
                />
              )}
            </FormControl>

            {/* Country */}
            <FormControl>
              <FormLabel fontSize="14px">Country</FormLabel>
              <Select
                options={COUNTRY_OPTIONS}
                value={form.country}
                onChange={handleCountryChange}
                placeholder="Select country..."
                styles={selectStyles}
                isSearchable
              />
            </FormControl>

            {/* City */}
            <FormControl>
              <FormLabel fontSize="14px">City</FormLabel>
              <Select
                options={cityOptions}
                value={form.city}
                onChange={val => setForm(p => ({ ...p, city: val }))}
                placeholder={form.country ? 'Select city...' : 'Select country first'}
                isDisabled={!form.country}
                styles={selectStyles}
                isSearchable
              />
            </FormControl>

            {/* Discovery Source */}
            <FormControl>
              <FormLabel fontSize="14px">How did you find us?</FormLabel>
              <Select
                options={DISCOVERY_OPTIONS}
                value={form.discoverySource}
                onChange={val => setForm(p => ({ ...p, discoverySource: val, discoveryOther: '' }))}
                placeholder="Select source..."
                styles={selectStyles}
                isSearchable
              />
              {form.discoverySource?.value === 'other' && (
                <Input
                  mt={2}
                  placeholder="Please tell us how you found us"
                  value={form.discoveryOther}
                  onChange={e => setForm(p => ({ ...p, discoveryOther: e.target.value }))}
                  h="44px"
                  borderRadius="12px"
                  borderColor="#E0E0E0"
                  _focus={{ borderColor: '#CF008A', boxShadow: '0 0 0 1px #CF008A' }}
                />
              )}
            </FormControl>

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            bg="#CF008A"
            color="white"
            borderRadius="12px"
            h="44px"
            px={8}
            _hover={{ bg: '#a8006e' }}
            onClick={handleSubmit}
            isLoading={isSaving}
            loadingText="Saving..."
            w="100%"
          >
            Get started
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
